/**
 * @name DialogOverlays
 * @description Controls global overlay navigation elements. Closely related to the dialog module.
 */

class DialogOverlays {

  /**
   * @param {HTMLElement} overlayNode - The element groups the overlay elements.
   */
  constructor(overlayNode) {
    this.overlayNode = overlayNode;
    this.overlayItemNodes = overlayNode.querySelectorAll('[data-overlay-item]');
    this.overlayToggles = document.querySelectorAll('[data-overlay-toggle]');
    this.openOverlays = [];

    this.init();
  }

  init() {
    this.overlayToggles.forEach((overlayToggle) => {
      const overlayId = overlayToggle.dataset.overlayToggle;
      overlayToggle.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleOverlay(overlayId);
      });
    });

    this.registerEventBus();
  }

  /**
   * Trigger toggle for the overlay item.
   * If the overlay item is already open, it will be closed.
   * If the overlay item is closed, it will be opened.
   * If no overlayId is provided, all overlays will be closed.
   * 
   * @param {string|null} overlayId 
   */
  toggleOverlay(overlayId) {
    const overlayItemNode = Array.from(this.overlayItemNodes).find(item => item.dataset.overlayItem === overlayId);

    if (!overlayItemNode) {
      this.openOverlays.forEach(item => {
        this.closeOverlay(item);
      });

      return
    }

    if (overlayItemNode.open) {
      this.closeOverlay(overlayItemNode);
    } else {
      this.openOverlay(overlayItemNode);
    }
  }

  /**
   * Open an overlay by its ID.
   * @param {string} overlayId 
   * @returns if overlay ID was not found.
   */
  openOverlayById(overlayId) {
    const overlayItemNode = Array.from(this.overlayItemNodes).find(item => item.dataset.overlayItem === overlayId);

    if (!overlayItemNode) {
      return;
    }
    this.openOverlay(overlayItemNode);
  }

  /**
   * Close an overlay by its ID.
   * If the overlay ID is not found, it will close all overlays.
   * @param {string} overlayId 
   * @returns 
   */
  closeOverlayById(overlayId) {
    const overlayItemNode = Array.from(this.overlayItemNodes).find(item => item.dataset.overlayItem === overlayId);

    if (!overlayItemNode) {
      return;
    }
    this.closeOverlay(overlayItemNode);
  }

  /**
   * Trigger open animation for the overlay item.
   * @param {HTMLDialogElement} overlayItemNode 
   */
  openOverlay(overlayItemNode) {
    if (!overlayItemNode || overlayItemNode.open) {
      return;
    }

    overlayItemNode.showModal();

    window.requestAnimationFrame(() => {
      overlayItemNode.classList.add('dialog--open');
      document.body.classList.add('er-scroll--lock');
    });

    overlayItemNode.addEventListener('keydown', this.handlePressKey.bind(this));
    overlayItemNode.addEventListener('click', this.handleBackdropClick.bind(this));

    // Add the overlay item to the list of open overlays
    this.openOverlays.push(overlayItemNode);
  }

  /**
   * Trigger close animation for the overlay item.
   * @param {HTMLDialogElement} overlayItemNode 
   */
  closeOverlay(overlayItemNode) {
    if (!overlayItemNode || !overlayItemNode.open) {
      return;
    }

    const closeOverlayAfterTransitioned = () => {
      overlayItemNode.close();

      document.body.classList.remove('er-scroll--lock');
      overlayItemNode.removeEventListener('click', this.handleBackdropClick);
      overlayItemNode.removeEventListener('transitionend', closeOverlayAfterTransitioned);
    };

    overlayItemNode.addEventListener('transitionend', closeOverlayAfterTransitioned);
    overlayItemNode.classList.remove('dialog--open');
    overlayItemNode.removeEventListener('keydown', this.handlePressKey.bind(this));
    
    // Remove the overlay from the recent overlays
    this.openOverlays = this.openOverlays.filter(item => item !== overlayItemNode);
  }

  handlePressKey(event) {
    if (event.key === 'Escape') {
      // Prevent default <dialog> behavior
      event.preventDefault();

      // Close the most top overlay
      if (this.openOverlays.length > 0) {
        const lastOverlay = this.openOverlays[this.openOverlays.length - 1];

        if (event.currentTarget === lastOverlay) {
          this.closeOverlay(this.openOverlays[this.openOverlays.length - 1]);
        }
      }
    }
  }

  handleBackdropClick(event) {
    if (event.currentTarget.dataset.overlayItem === 'tu-message') {
      return;
    }

    if (event.target === event.currentTarget) {
      this.openOverlays.forEach(item => {
        this.closeOverlay(item);
      });
    }
  }

  registerEventBus() {
    const overlayCookieId = this.overlayNode.dataset.overlayCookieMessage;
    const overlayLangId = this.overlayNode.dataset.overlayLang;
    const overlaySearchId = this.overlayNode.dataset.overlaySearch;
    const overlayTuId = this.overlayNode.dataset.overlayTuMessage;
    const phaseoutMessageId = this.overlayNode.dataset.overlayPhaseoutMessage;

    document.addEventListener('showCookieMessage', () => this.openOverlayById(overlayCookieId));
    document.addEventListener('hideCookieMessage', () => this.closeOverlayById(overlayCookieId));
    document.addEventListener('showLangSelect', () => this.openOverlayById(overlayLangId));
    document.addEventListener('hideLangSelect', () => this.closeOverlayById(overlayLangId));
    document.addEventListener('showSearch', () => this.openOverlayById(overlaySearchId));
    document.addEventListener('hideSearch', () => this.closeOverlayById(overlaySearchId));
    document.addEventListener('showTUMessage', () => this.openOverlayById(overlayTuId));
    document.addEventListener('hideTUMessage', () => this.closeOverlayById(overlayTuId));
    document.addEventListener('showPhaseoutMessage', () => this.openOverlayById(phaseoutMessageId));
    document.addEventListener('hidePhaseoutMessage', () => this.closeOverlayById(phaseoutMessageId));
  }

  /**
  * @description Initializes all elements with the data-overlay attribute
  */
  static initOverlays() {
    const triggerOverlayElements = document.querySelectorAll('[data-overlay]');

    triggerOverlayElements.forEach((element) => {
      new DialogOverlays(element);
    });
  }
}

document.addEventListener('DOMContentLoaded', DialogOverlays.initOverlays);
